import * as React from 'react';
import { useIntl } from 'react-intl';

import * as Styled from './text-button.styles';
import type { TextButtonProps } from './text-button.types';

export const TextButton = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  TextButtonProps
>(
  (
    {
      children,
      className,
      disabled = false,
      ellipsis,
      error,
      fontWeight,
      href,
      icon: Icon,
      iconAfter,
      innerRef,
      loading,
      onClick,
      size = 'medium',
      sizeConfined,
      sizeWide,
      to,
      type,
      ...props
    },
    forwardedRef
  ) => {
    const intl = useIntl();

    const iconSize = size === 'small' ? 18 : 24;
    const iconOnly = !children;
    const fullText = typeof children === 'string' ? children : undefined;

    const combinedRef = React.useMemo(() => {
      if (forwardedRef) {
        return typeof forwardedRef === 'function'
          ? { current: null, set: forwardedRef }
          : forwardedRef;
      }
      return innerRef;
    }, [forwardedRef, innerRef]);

    const commonProps = {
      $ellipsis: ellipsis,
      $error: error,
      $fontWeight: fontWeight,
      $iconAfter: iconAfter,
      $iconOnly: iconOnly,
      $size: size,
      $sizeConfined: sizeConfined,
      $sizeWide: sizeWide,
      'aria-busy': loading,
      'aria-disabled': disabled || loading,
      'aria-label': loading
        ? intl.formatMessage({
            id: 'XDS_A11Y_LOADING',
            defaultMessage: 'Loading',
          })
        : undefined,
      'data-loading': loading,
      'data-xds': 'TextButton',
      className,
      disabled: disabled || loading,
      onClick,
      ref: combinedRef,
      title: ellipsis && fullText ? fullText : undefined,
    };

    const buttonContent = (
      <Styled.InnerContent $ellipsis={ellipsis}>
        {!loading && Icon && (
          <Styled.IconContainer $iconSize={iconSize} $iconAfter={iconAfter}>
            <Icon width={iconSize} aria-hidden="true" height={iconSize} />
          </Styled.IconContainer>
        )}
        {loading && (
          <Styled.Loading
            $size={size}
            $sizeConfined={sizeConfined}
            $sizeWide={sizeWide}
            aria-hidden="true"
          />
        )}
        <Styled.Text $ellipsis={ellipsis} $loading={loading}>
          {children}
        </Styled.Text>
      </Styled.InnerContent>
    );

    if (to || href) {
      return (
        <Styled.RouterTextButton
          {...(to && { to })}
          {...(href && { href })}
          {...commonProps}
          {...props}
        >
          {buttonContent}
        </Styled.RouterTextButton>
      );
    }

    return (
      <Styled.TextButton type={type} {...commonProps} {...props}>
        {buttonContent}
      </Styled.TextButton>
    );
  }
);

TextButton.displayName = 'TextButton';
